import type {
  SessionTitles,
  Session,
  ProcessesListResponse,
  Process,
  GoldenStandardListResponse,
  GoldenStandardItem,
  GoldenStandardCreateRequest,
  GoldenStandardUpdateRequest,
  DuplicateMatch,
} from "../types";
import { axiosInstance } from "./apiClient";
import { toApiError } from "../utils/llmAvailability";

const TOKEN_KEY = "access_token";

// Login API
export async function login(
  username: string,
  password: string
): Promise<string> {
  try {
    const response = await axiosInstance.post("/auth/login", {
      username,
      password,
    });
    const token = response.data.access_token;
    // Salva il token nel localStorage
    localStorage.setItem(TOKEN_KEY, token);
    return token;
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.detail || error.message || "Login failed";
    throw new Error(errorMessage);
  }
}

// Logout
export function logout(): void {
  localStorage.removeItem(TOKEN_KEY);
}

// Check if user is authenticated
export function isAuthenticated(): boolean {
  return !!localStorage.getItem(TOKEN_KEY);
}

// Get stored token
export function getToken(): string | null {
  return localStorage.getItem(TOKEN_KEY);
}

export interface CurrentUser {
  id: string;
  email: string;
  firstname?: string | null;
  lastname?: string | null;
  role?: string;
  is_active: boolean;
  is_verified: boolean;
}

export async function updateCurrentUserProfile(payload: {
  firstname?: string | null;
  lastname?: string | null;
  email?: string | null;
}): Promise<CurrentUser> {
  const response = await axiosInstance.patch<CurrentUser>("/auth/me", payload);
  return response.data;
}

export async function changeCurrentUserPassword(payload: {
  current_password: string;
  new_password: string;
  new_password_confirm: string;
}): Promise<{ message: string }> {
  const response = await axiosInstance.post<{ message: string }>(
    "/auth/change-password",
    payload
  );
  return response.data;
}

// Helper per ottenere l'URL base (per streaming che usa fetch)
function getBaseURL(): string {
  return axiosInstance.defaults.baseURL || "http://localhost:8000";
}

// Sessions API
export async function getSessionTitles(): Promise<SessionTitles> {
  const response = await axiosInstance.get<SessionTitles>("/sessions/titles");
  return response.data;
}

export async function getSession(sessionId: string): Promise<Session> {
  const response = await axiosInstance.get<Session>(
    `/sessions/session/${sessionId}`
  );
  return response.data;
}

export async function deleteSession(sessionId: string): Promise<void> {
  await axiosInstance.delete(`/sessions/session/${sessionId}`);
}

// Agent API (SSE stream)
export function createAnswerStream(
  question: string,
  sessionId: string,
  collectionName: string = "chunks",
  userLocation?: string
): EventSource {
  const params = new URLSearchParams({
    question,
    session_id: sessionId,
    collection_name: collectionName,
  });
  if (userLocation) {
    params.append("user_location", userLocation);
  }

  // EventSource non supporta headers custom, quindi per l'autenticazione
  // usiamo streamAnswer() con fetch. Questo metodo è un fallback.
  return new EventSource(
    `${getBaseURL()}/agent/answer_stream?${params.toString()}`
  );
}

// Fetch-based streaming per supportare autenticazione
export async function* streamAnswer(
  question: string,
  sessionId: string,
  collectionName: string = "chunks",
  userLocation?: string
): AsyncGenerator<{ type: string; data: unknown }> {
  const params = new URLSearchParams({
    question,
    session_id: sessionId,
    collection_name: collectionName,
  });
  if (userLocation) {
    params.append("user_location", userLocation);
  }

  const token = getToken();
  const headers: HeadersInit = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(
    `${getBaseURL()}/agent/answer_stream?${params.toString()}`,
    {
      headers,
    }
  );

  if (res.status === 401) {
    logout();
    window.location.reload();
    throw new Error("Session expired. Please login again.");
  }

  if (!res.ok) {
    throw new Error(`Stream failed: ${res.status}`);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));
          yield data;
        } catch {
          // Skip malformed JSON
        }
      }
    }
  }
}

/** Opzioni per create chat / send message (agent answer_stream) */
export interface ChatStreamOptions {
  short_answer?: boolean; // Esaustiva (false) o breve (true), default false
  use_pro_model?: boolean; // Veloce (false) o Performante (true), default false
}

// Chat API - nuovo endpoint che crea chat e ritorna stream
export async function* createChat(
  question: string,
  options?: ChatStreamOptions
): AsyncGenerator<{ type: string; data: unknown }> {
  const token = getToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${getBaseURL()}/chats`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      question,
      short_answer: options?.short_answer ?? false,
      use_pro_model: options?.use_pro_model ?? false,
    }),
  });

  if (res.status === 401) {
    logout();
    window.location.reload();
    throw new Error("Session expired. Please login again.");
  }

  if (!res.ok) {
    const errorText = await res
      .text()
      .catch(() => `Stream failed: ${res.status}`);
    throw new Error(errorText);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));
          yield data;
        } catch {
          // Skip malformed JSON
        }
      }
    }
  }
}

/** Crea solo la chat (senza messaggio). Ritorna { id }. Poi il front naviga e chiama send-message. */
export async function createChatOnly(
  options?: ChatStreamOptions
): Promise<{ id: string }> {
  const response = await axiosInstance.post<{ id: string }>("/chats/create", {
    short_answer: options?.short_answer ?? false,
    use_pro_model: options?.use_pro_model ?? false,
  });
  return response.data;
}

// Chat API - ottiene tutte le chat dell'utente
export interface Chat {
  id: string;
  user_id: string;
  title: string | null;
  short_answer: boolean;
  use_pro_model: boolean;
  has_streaming?: boolean;
  create_date: string;
  update_date: string;
}

export interface ChatSettings {
  short_answer: boolean | null;
  use_pro_model: boolean | null;
}

export async function getChats(): Promise<Chat[]> {
  const response = await axiosInstance.get<Chat[]>("/chats");
  return response.data;
}

export async function searchChats(find: string): Promise<Chat[]> {
  const response = await axiosInstance.get<Chat[]>("/chats/search", {
    params: { find },
  });
  return response.data;
}

export interface ChatInfo {
  short_answer: boolean | null;
  use_pro_model: boolean | null;
  has_streaming: boolean;
  last_message: string | null;
  create_date: string;
  update_date: string;
}

export async function getChatInfo(chatId: string): Promise<ChatInfo> {
  const response = await axiosInstance.get<ChatInfo>(`/chats/info/${chatId}`);
  return response.data;
}

export async function updateChatSettings(
  chatId: string,
  payload: {
    short_answer?: boolean;
    use_pro_model?: boolean;
  }
): Promise<ChatSettings> {
  const response = await axiosInstance.patch<ChatSettings>(
    `/chats/${chatId}`,
    payload
  );
  return response.data;
}

// Chat API - ottiene una chat specifica (chiama il client AI per la sessione)
export async function getChat(chatId: string): Promise<any> {
  const response = await axiosInstance.get(`/chats/${chatId}`);
  return response.data;
}

// Feedback like/dislike su messaggi AI
export interface FeedbackItem {
  messageId: string;
  value: number; // 1 like, -1 dislike
}

export async function getFeedback(
  chatId: string
): Promise<{ items: FeedbackItem[] }> {
  const response = await axiosInstance.get<{ items: FeedbackItem[] }>(
    `/feedback?chatId=${encodeURIComponent(chatId)}`
  );
  return response.data;
}

export async function feedbackLike(
  chatId: string,
  messageId: string,
  comment?: string | null
): Promise<FeedbackItem> {
  const response = await axiosInstance.post<FeedbackItem>("/feedback/like", {
    chatId,
    messageId,
    comment: comment ?? undefined,
  });
  return response.data;
}

export async function feedbackDislike(
  chatId: string,
  messageId: string,
  comment?: string | null
): Promise<FeedbackItem> {
  const response = await axiosInstance.post<FeedbackItem>("/feedback/dislike", {
    chatId,
    messageId,
    comment: comment ?? undefined,
  });
  return response.data;
}

export async function feedbackReset(
  chatId: string,
  messageId: string
): Promise<{ messageId: string; value: number }> {
  const response = await axiosInstance.post<{
    messageId: string;
    value: number;
  }>("/feedback/reset", { chatId, messageId });
  return response.data;
}

// Chat API - truncate + fork (nuova sessione), usato da Riprova e Edit
export async function truncateChat(
  chatId: string,
  messageId: string
): Promise<{ chatId: string }> {
  const response = await axiosInstance.post<{ chatId: string }>(
    "/chats/truncate",
    { chatId, messageId }
  );
  return response.data;
}

// Chat API - elimina una chat
export async function deleteChat(chatId: string): Promise<void> {
  try {
    await axiosInstance.delete(`/chats/${chatId}`);
  } catch (error: any) {
    const errorMessage =
      error.response?.data?.detail ||
      error.message ||
      "Errore durante l'eliminazione";
    throw new Error(errorMessage);
  }
}

export async function renameChat(chatId: string, title: string): Promise<Chat> {
  const response = await axiosInstance.post<Chat>("/chats/rename", {
    chatId,
    title,
  });
  return response.data;
}

// Chat API - invia messaggio a una chat esistente
export async function* sendChatMessage(
  chatId: string,
  question: string,
  options?: ChatStreamOptions
): AsyncGenerator<{ type: string; data: unknown }> {
  const token = getToken();
  const headers: HeadersInit = {
    "Content-Type": "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${getBaseURL()}/chats/${chatId}/send-message`, {
    method: "POST",
    headers,
    body: JSON.stringify({
      question,
      short_answer: options?.short_answer ?? false,
      use_pro_model: options?.use_pro_model ?? false,
    }),
  });

  if (res.status === 401) {
    logout();
    window.location.reload();
    throw new Error("Session expired. Please login again.");
  }

  if (!res.ok) {
    const errorText = await res
      .text()
      .catch(() => `Stream failed: ${res.status}`);
    throw new Error(errorText);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));
          yield data;
        } catch {
          // Skip malformed JSON
        }
      }
    }
  }
}

export async function* streamChatRecovery(
  chatId: string
): AsyncGenerator<{ type: string; data: unknown }> {
  const token = getToken();
  const headers: HeadersInit = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const res = await fetch(`${getBaseURL()}/chats/${chatId}/streaming-chat`, {
    method: "GET",
    headers,
  });

  if (!res.ok) {
    throw new Error(`Recovery stream failed: ${res.status}`);
  }

  const reader = res.body?.getReader();
  if (!reader) throw new Error("No response body");

  const decoder = new TextDecoder();
  let buffer = "";

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    buffer = lines.pop() || "";

    for (const line of lines) {
      if (line.startsWith("data: ")) {
        try {
          const data = JSON.parse(line.slice(6));
          yield data;
        } catch {
          // Skip malformed JSON
        }
      }
    }
  }
}

// Embedding/Processes API
export async function getProcesses(): Promise<ProcessesListResponse> {
  const response = await axiosInstance.get<ProcessesListResponse>(
    "/embedding/processes"
  );
  return response.data;
}

export async function getProcess(processId: string): Promise<Process> {
  const response = await axiosInstance.get<Process>(
    `/embedding/processes/process/${processId}`
  );
  return response.data;
}

export async function deleteProcess(processId: string): Promise<void> {
  await axiosInstance.delete(`/embedding/processes/process/${processId}`);
}

export async function uploadDocument(
  file: File,
  options: {
    collectionName?: string;
    goldenStandard?: boolean;
    forceTitle?: string;
    forceDate?: string;
    forceCategory?: string;
    fileId?: string;
  } = {}
): Promise<{ process_id: string; collection: string }> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("collection_name", options.collectionName || "chunks");
  formData.append(
    "callback_url",
    `${window.location.origin}/api/callback/notify_test`
  );
  formData.append("golden_standard", String(options.goldenStandard || false));

  if (options.forceTitle) formData.append("force_title", options.forceTitle);
  if (options.forceDate) formData.append("force_date", options.forceDate);
  if (options.forceCategory)
    formData.append("force_category", options.forceCategory);
  if (options.fileId) formData.append("file_id", options.fileId);

  const response = await axiosInstance.post<{
    process_id: string;
    collection: string;
  }>("/embedding/", formData, {
    headers: {
      "Content-Type": "multipart/form-data",
    },
  });

  return response.data;
}

// Metadata extraction
export async function extractMetadata(
  file: File,
  numPages: number = 2
): Promise<{
  title: string;
  date: string | null;
  category: string;
  confidence: number | null;
}> {
  const formData = new FormData();
  formData.append("file", file);
  formData.append("num_pages", String(numPages));

  try {
    const response = await axiosInstance.post<{
      title: string;
      date: string | null;
      category: string;
      confidence: number | null;
    }>("/documents/metadata", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
      timeout: 90_000,
    });

    return response.data;
  } catch (error: unknown) {
    throw toApiError(error, "Impossibile estrarre i metadati del documento");
  }
}

// Categories
export interface CategoryInfo {
  name: string;
  description: string;
}

export interface GoldenStandardExtractResponse {
  title: string;
  text: string;
  category: string;
  date: string | null;
  confidence?: number | null;
}

export async function extractGoldenStandardText(
  file: File
): Promise<GoldenStandardExtractResponse> {
  const formData = new FormData();
  formData.append("file", file);
  try {
    const response = await axiosInstance.post<GoldenStandardExtractResponse>(
      "/golden-standards/extract-text",
      formData,
      {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 90_000,
      }
    );
    return response.data;
  } catch (error: unknown) {
    throw toApiError(error, "Errore estrazione testo");
  }
}

export async function getGoldenStandards(params?: {
  skip?: number;
  limit?: number;
}): Promise<GoldenStandardListResponse> {
  const response = await axiosInstance.get<GoldenStandardListResponse>(
    "/golden-standards",
    {
      params: {
        skip: params?.skip ?? 0,
        limit: params?.limit ?? 500,
      },
    }
  );
  return response.data;
}

export async function getGoldenStandard(
  chunkId: number
): Promise<GoldenStandardItem> {
  const response = await axiosInstance.get<GoldenStandardItem>(
    `/golden-standards/${chunkId}`
  );
  return response.data;
}

export async function createGoldenStandard(
  request: GoldenStandardCreateRequest
): Promise<{ chunk_id: number; message: string }> {
  const response = await axiosInstance.post<{
    chunk_id: number;
    message: string;
  }>("/golden-standards", request);
  return response.data;
}

export async function updateGoldenStandard(
  chunkId: number,
  request: GoldenStandardUpdateRequest
): Promise<{ message: string }> {
  const response = await axiosInstance.patch<{ message: string }>(
    `/golden-standards/${chunkId}`,
    request
  );
  return response.data;
}

export async function deleteGoldenStandard(chunkId: number): Promise<void> {
  await axiosInstance.delete(`/golden-standards/${chunkId}`);
}

/** Verifica duplicati/simili prima di creare una Golden Standard. Non salva nulla. */
export async function checkGoldenStandardDuplicates(
  request: GoldenStandardCreateRequest
): Promise<{ matches: DuplicateMatch[] }> {
  const response = await axiosInstance.post<{ matches: DuplicateMatch[] }>(
    "/golden-standards/check-duplicates",
    request
  );
  return response.data;
}

// ============================================================================
// Knowledge Base API
// ============================================================================

export interface Section {
  id: string;
  date: string;
  status: "in corso" | "completato" | "fallito";
  estimatedTime: string;
  documentCount: number;
  documents: Document[];
}

export interface Document {
  id: string;
  categoryId: string;
  name: string;
  publicationDate?: string;
  loadingDate?: string;
  status?: "caricato" | "in progress" | "fallito";
}

export interface DocumentCategory {
  id: string;
  name: string;
}

export async function getSections(): Promise<Section[]> {
  const response = await axiosInstance.get<Section[]>(
    "/knowledge-base/sections"
  );
  return response.data;
}

export async function getDocuments(params?: {
  id?: string;
  categoryId?: string;
  name?: string;
}): Promise<Document[]> {
  const queryParams = new URLSearchParams();
  if (params?.id) queryParams.append("id", params.id);
  if (params?.categoryId) queryParams.append("category_id", params.categoryId);
  if (params?.name) queryParams.append("name", params.name);

  const response = await axiosInstance.get<Document[]>(
    `/knowledge-base/documents?${queryParams.toString()}`
  );
  return response.data;
}

export async function getDocumentCategories(): Promise<DocumentCategory[]> {
  const response = await axiosInstance.get<DocumentCategory[]>(
    "/knowledge-base/document-categories"
  );
  return response.data;
}

export async function updateDocumentCategory(
  documentId: string,
  categoryId: string
): Promise<void> {
  await axiosInstance.patch(
    `/knowledge-base/documents/${documentId}/category`,
    { category_id: categoryId }
  );
}

export async function deleteDocument(documentId: string): Promise<void> {
  await axiosInstance.delete(`/knowledge-base/documents/${documentId}`);
}

export async function retryDocument(documentId: string): Promise<void> {
  await axiosInstance.post(`/knowledge-base/documents/${documentId}/retry`);
}

// ============================================================================
// Folders API
// ============================================================================

export interface Folder {
  id: string;
  name: string;
  parent_id: string | null;
  order: number | null;
  type: 0 | 1; // 0 = folder, 1 = chat
  chat_id: string | null;
  user_id: string;
  children?: Folder[];
}

export async function getFolders(): Promise<Folder[]> {
  const response = await axiosInstance.get<Folder[]>("/folders");
  return response.data;
}

export async function createFolder(data: {
  name: string;
  parent_id?: string | null;
  order?: number | null;
  type?: 0 | 1;
  chat_id?: string | null;
}): Promise<Folder> {
  const response = await axiosInstance.post<Folder>("/folders", data);
  return response.data;
}

export async function updateFolderParent(
  folderId: string,
  parentId: string | null,
  order?: number
): Promise<Folder> {
  const response = await axiosInstance.patch<Folder>(
    `/folders/${folderId}/parent`,
    {
      parent_id: parentId,
      order,
    }
  );
  return response.data;
}

export async function renameFolder(
  folderId: string,
  name: string
): Promise<Folder> {
  const response = await axiosInstance.patch<Folder>(
    `/folders/${folderId}/rename`,
    {
      name,
    }
  );
  return response.data;
}

export async function deleteFolder(folderId: string): Promise<void> {
  await axiosInstance.delete(`/folders/${folderId}`);
}

// ============================================================================
// Upload Sessions API (Knowledge Base - admin/maintainer)
// ============================================================================

export interface UploadSessionItem {
  id: string;
  created_by: string;
  status: string;
  total_progress: number | null;
  document_count: number;
  created_at: string;
  updated_at: string;
}

export interface SessionStatusPayload {
  session_id: string;
  status: string;
  total_progress: number;
  current_step_label?: string;
  files: {
    id: string;
    title: string;
    status: string;
    progress: number;
    step_label?: string;
    failure_code?: string | null;
    failure_message?: string | null;
  }[];
}

export async function getUploadSessions(): Promise<UploadSessionItem[]> {
  const response = await axiosInstance.get<UploadSessionItem[]>("/sessions");
  return response.data;
}

export interface UploadDocumentItem {
  id: string;
  upload_session_id: string;
  title: string;
  publication_date: string | null;
  category_id: string | null;
  status: string;
  progress: number | null;
  step_label?: string;
  failure_code?: string | null;
  failure_message?: string | null;
  created_at: string;
}

export async function getSessionDocuments(
  sessionId: string
): Promise<UploadDocumentItem[]> {
  const response = await axiosInstance.get<UploadDocumentItem[]>(
    `/sessions/${sessionId}/documents`
  );
  return response.data;
}

/** Riprova embedding per un documento: DELETE processo vecchio, POST embedding, nuovo process_id. */
export async function retrySessionDocument(documentId: string): Promise<void> {
  await axiosInstance.post(`/sessions/documents/${documentId}/retry`);
}

/** Elimina un documento dalla sessione: cancella processo embedding, file su disco e record nel DB. */
export async function deleteSessionDocument(documentId: string): Promise<void> {
  await axiosInstance.delete(`/sessions/documents/${documentId}`);
}

export async function createUploadSession(
  files: File[],
  metadata: {
    title?: string;
    publication_date?: string;
    category_id?: string;
  }[]
): Promise<UploadSessionItem> {
  const form = new FormData();
  form.append(
    "metadata_json",
    JSON.stringify(metadata.length ? metadata : files.map(() => ({})))
  );
  files.forEach((f) => form.append("files", f));
  const response = await axiosInstance.post<UploadSessionItem>(
    "/sessions",
    form,
    {
      headers: { "Content-Type": "multipart/form-data" },
    }
  );
  return response.data;
}

/** Verifica per ciascun file se esiste già in archivio un duplicato o un documento molto simile. Non salva nulla. */
export async function checkUploadDuplicates(
  files: File[],
  metadata: {
    title?: string;
    publication_date?: string;
    category_id?: string;
  }[]
): Promise<{ results: { filename: string; matches: DuplicateMatch[] }[] }> {
  const form = new FormData();
  form.append(
    "metadata_json",
    JSON.stringify(metadata.length ? metadata : files.map(() => ({})))
  );
  files.forEach((f) => form.append("files", f));
  const response = await axiosInstance.post<{
    results: { filename: string; matches: DuplicateMatch[] }[];
  }>("/sessions/check-duplicates", form, {
    headers: { "Content-Type": "multipart/form-data" },
  });
  return response.data;
}

/** Elimina una sessione di upload (chiama DELETE /sessions e BE AI embedding/sessions delete). */
export async function deleteUploadSession(sessionId: string): Promise<void> {
  await axiosInstance.delete(`/sessions/${sessionId}`);
}

/** Consume SSE stream for session status; onEach receives parsed SessionStatusPayload. Call returned function to abort. */
export function streamUploadSessionStatus(
  sessionId: string,
  onEach: (data: SessionStatusPayload) => void,
  onError?: (err: unknown) => void
): () => void {
  const baseURL = axiosInstance.defaults.baseURL || "";
  const url = `${baseURL}/sessions/${sessionId}/status`;
  const token = getToken();
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const ac = new AbortController();
  fetch(url, { headers, signal: ac.signal })
    .then(async (res) => {
      if (!res.ok) {
        onError?.(new Error(`HTTP ${res.status}`));
        return;
      }
      const reader = res.body?.getReader();
      if (!reader) return;
      const decoder = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6)) as SessionStatusPayload;
              onEach(data);
            } catch (_) {}
          }
        }
      }
    })
    .catch((e) => {
      if (e?.name !== "AbortError") onError?.(e);
    });
  return () => ac.abort();
}

// ============================================================================
// Knowledge Base: categories (gestionale/categories - admin/maintainer)
// ============================================================================

/** Risposta BE: value + description. Label calcolata lato FE (useCategories). */
export interface CategoryRaw {
  value: string;
  description: string;
}

/** Categoria per UI: value, label (formattata), description. */

export async function getCategories(): Promise<CategoryRaw[]> {
  const response = await axiosInstance.get<CategoryRaw[]>(
    "/documents/categories"
  );
  return response.data;
}

/**
 * Sottoinsieme delle categorie che l'utente corrente è autorizzato a usare in
 * upload/Golden Standard (tutte se nessuna restrizione è stata applicata).
 */
export async function getAllowedCategories(): Promise<CategoryRaw[]> {
  const response = await axiosInstance.get<CategoryRaw[]>(
    "/documents/categories/allowed"
  );
  return response.data;
}

// ============================================================================
// Permessi categoria per utente (Feature 4 - solo admin)
// ============================================================================

export interface UserCategoryPermissionsResponse {
  category_names: string[];
}

/** Categorie consentite per l'utente indicato (vuoto = nessuna restrizione). */
export async function getUserCategoryPermissions(
  userId: string
): Promise<string[]> {
  const response = await axiosInstance.get<UserCategoryPermissionsResponse>(
    `/admin/users/${userId}/categories`
  );
  return response.data.category_names;
}

/** Sostituisce l'intero set di categorie consentite per l'utente (vuoto = rimuove restrizione). */
export async function setUserCategoryPermissions(
  userId: string,
  categoryNames: string[]
): Promise<string[]> {
  const response = await axiosInstance.put<UserCategoryPermissionsResponse>(
    `/admin/users/${userId}/categories`,
    { category_names: categoryNames }
  );
  return response.data.category_names;
}

// ============================================================================
// Knowledge Base documents (embedding/files - admin/maintainer)
// ============================================================================

export interface KbFileItem {
  file_id: string;
  document_count: number;
  file_title: string | null;
  category: string | null;
  datetime_: string | null;
  hosted_file_name: string | null;
  type: "document" | "golden_standard";
  created_at?: string | null;
  updated_at?: string | null;
}

export interface KbFilesListResponse {
  items: KbFileItem[];
  total: number;
}

export async function getKbDocuments(params?: {
  skip?: number;
  limit?: number;
}): Promise<KbFilesListResponse> {
  const response = await axiosInstance.get<KbFilesListResponse>("/documents", {
    params: { skip: params?.skip ?? 0, limit: params?.limit ?? 5000 },
  });
  return response.data;
}

/** Aggiorna metadati file in KB (PATCH embedding/files sul BE AI; la categoria è gestita dal BE AI). */
export async function updateKbDocumentMetadata(
  fileId: string,
  data: {
    category?: string;
    file_title?: string;
    datetime_?: string;
    hosted_file_name?: string;
  }
): Promise<void> {
  await axiosInstance.patch(`/documents/${fileId}`, data);
}

export async function deleteKbDocument(fileId: string): Promise<void> {
  await axiosInstance.delete(`/documents/${fileId}`);
}

/**
 * Scarica il file di un documento dalla KB (GET /sessions/documents/{fileId}/download).
 * suggestedFileName: nome da usare per il file salvato (es. file_title o hosted_file_name).
 */
// ============================================================================
// System API (estrattore + RAM)
// ============================================================================

export interface ExtractorAwakeResponse {
  awake: boolean;
  attempts: number;
  message: string;
}

export interface RamStatsResponse {
  total_gb: number;
  used_gb: number;
  available_gb: number;
  percent_used: number;
}

/** Sveglia l'estrattore remoto: poll fino a 60 volte (max ~3 min), restituisce awake + attempts. */
export async function awakenExtractor(): Promise<ExtractorAwakeResponse> {
  const response = await axiosInstance.post<ExtractorAwakeResponse>(
    "/system/extractor/awake",
    null,
    { timeout: 200_000 }
  );
  return response.data;
}

/** Statistiche RAM correnti del server backend. */
export async function getServerRam(): Promise<RamStatsResponse> {
  const response = await axiosInstance.get<RamStatsResponse>("/system/ram");
  return response.data;
}

export async function downloadKbDocument(
  fileId: string,
  suggestedFileName?: string | null
): Promise<void> {
  const response = await axiosInstance.get(
    `/sessions/documents/${fileId}/download`,
    {
      responseType: "blob",
    }
  );
  const contentDisposition = response.headers["content-disposition"];
  let filename = suggestedFileName || fileId;
  if (contentDisposition) {
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
      contentDisposition
    );
    if (match?.[1]) filename = match[1].replace(/['"]/g, "").trim();
  }
  if (!filename.toLowerCase().endsWith(".pdf")) filename = `${filename}.pdf`;
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}

/** Scarica il PDF on-demand del report mensile feedback (solo admin). */
export async function downloadFeedbackReport(): Promise<void> {
  const response = await axiosInstance.get("/admin/feedback-report", {
    responseType: "blob",
  });
  const contentDisposition = response.headers["content-disposition"];
  let filename = "report_feedback.pdf";
  if (contentDisposition) {
    const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(
      contentDisposition
    );
    if (match?.[1]) filename = match[1].replace(/['"]/g, "").trim();
  }
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
}
